<template>
  <v-card
    flat
    outlined
    rounded="lg"
    height="100%"
    color="cardbg"
    class="flex"
    elevation="10"
  >
    <v-card-title
      :style="`padding: calc(${scale} * 0.4rem);
        line-height: calc(${scale} * 0.75rem);
        font-size: calc(${scale} * 0.7rem);`"
      class="font-weight-medium d-flex justify-space-between"
    >
      <span class="titlecolor--text">
        {{ title }}
      </span>
      <span class="subtitlecolor--text">{{ daterange }}</span>
    </v-card-title>
    <v-card-text
      :style="`padding: calc(${scale} * 0.25rem);`"
      style="height: 90%; line-height: normal;"
    >
      <WidgetLoading v-if="loading" />
      <highcharts
        v-else
        :options="options"
        style="height: 105%; width: 100%;"
        :key="uuid"
      ></highcharts>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import WidgetLoading from './WidgetLoading.vue';

export default {
  name: 'ColumnChart',
  props: {
    title: { type: String },
    daterange: { type: String },
    scale: { type: Number, default: 1 },
    unit: { type: String },
    loading: { type: Boolean, default: false },
    legend: { type: Boolean, default: false },
  },
  components: {
    WidgetLoading,
  },
  data() {
    return {
      uuid: 'uuid',
      options: {
        chart: {
          type: 'column',
          backgroundColor: 'transparent',
          style: {
            fontFamily: 'Poppins, "Open Sans", sans-serif',
          },
        },
        plotOptions: {
          series: {
            borderWidth: 0,
            dataLabels: {
              enabled: false,
              style: {
                textOutline: 'none',
              },
            },
          },
        },
        title: { text: '' },
        xAxis: {
          categories: [],
          labels: {
            reserveSpace: true,
            //  y: 50,
          },
          type: 'category',
          crosshair: true,
          title: { text: '' },
        },
        yAxis: {
          min: 0,
          max: 100,
          title: { text: '' },
          labels: {},
          gridLineColor: '#E0E0E0',
          plotLines: [],
        },
        legend: {
          enabled: this.legend,
        },
        series: [],
      },
    };
  },
  computed: {
    ...mapState('cellwiseProductionDashboard', ['theme']),
    ...mapGetters('cellwiseProductionDashboard', ['activeTheme']),
  },
  watch: {
    scale(val) {
      this.adjustSize(val);
      this.createUUID();
    },
    activeTheme() {
      this.applyTheme();
      this.createUUID();
    },
  },
  mounted() {
    this.applyTheme();
  },
  methods: {
    assignData(result) {
      this.options.xAxis.categories = result.categories;
      this.options.series = result.series;
      if (result.plotLines) this.options.yAxis.plotLines = result.plotLines;
      this.options.chart.type = result.charttype ? result.charttype : 'column';
      if (result.plotOptions) {
        this.options.plotOptions = result.plotOptions;
      }
      this.adjustSize(this.scale);
      this.applyTheme();
      this.createUUID();
    },
    applyTheme() {
      const themes = this.theme && this.theme.themes;
      const currentTheme = themes && themes[this.activeTheme]
        ? themes[this.activeTheme]
        : { subtitlecolor: '#A1A3A5', titlecolor: '#FFFFFF' };

      const gridColor = this.activeTheme === 'dark' ? '#495767' : '#E0E0E0';
      this.options.yAxis.gridLineColor = gridColor;

      if (this.options.xAxis.labels) {
        this.options.xAxis.labels.style = {
          ...this.options.xAxis.labels.style,
          color: currentTheme.subtitlecolor,
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        };
      }
      if (this.options.yAxis.labels) {
        this.options.yAxis.labels.style = {
          ...this.options.yAxis.labels.style,
          color: currentTheme.subtitlecolor,
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        };
      }

      const plotOpts = this.options.plotOptions;
      if (plotOpts && plotOpts.series && plotOpts.series.dataLabels) {
        plotOpts.series.dataLabels.style = {
          ...plotOpts.series.dataLabels.style,
          color: '#FFFFFF',
          textOutline: '1px contrast',
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        };
      }
    },
    adjustSize(scale) {
      const lablesize = 9 * scale;

      const padding = this.options.chart.type === 'column' ? 15 * scale : null;

      if (this.options.xAxis.labels) {
        this.options.xAxis.labels.y = padding;
        this.options.xAxis.labels.style = {
          ...this.options.xAxis.labels.style,
          fontSize: `${lablesize}px`,
        };
      }
      if (this.options.yAxis.labels) {
        this.options.yAxis.labels.style = {
          ...this.options.yAxis.labels.style,
          fontSize: `${lablesize}px`,
        };
      }

      this.options.legend.itemStyle = { fontSize: `${lablesize}px` };
      this.options.legend.symbolWidth = lablesize;
      this.options.legend.symbolPadding = lablesize;

      const plotOpts = this.options.plotOptions;
      if (plotOpts && plotOpts.series && plotOpts.series.dataLabels) {
        plotOpts.series.dataLabels.style = {
          ...plotOpts.series.dataLabels.style,
          fontSize: `${lablesize}px`,
        };
      }

      if (this.options.yAxis.plotLines && this.options.yAxis.plotLines[0]) {
        this.options.yAxis.plotLines[0].label.style.fontSize = `${lablesize}px`;
      }
      if (this.options.yAxis.plotLines && this.options.yAxis.plotLines[1]) {
        this.options.yAxis.plotLines[1].label.style.fontSize = `${lablesize}px`;
      }
    },
    createUUID() {
      let dt = new Date().getTime();
      const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // eslint-disable-next-line
        const r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        // eslint-disable-next-line
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
      });
      this.uuid = uuid;
    },
  },
};
</script>

<style lang="scss" scoped>
.w-full {
  width: 100%;
}
.h-full {
  height: 100%;
}
</style>
